import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import AdminLayout from './AdminLayout';
import './ApplicationsList.css';

const STAR = (n, rating) =>
  Array.from({ length: 5 }, (_, i) => (
    <span key={i} style={{ color: i < rating ? '#f59e0b' : '#d1d5db', fontSize: '1rem' }}>★</span>
  ));

const StatusBadge = ({ approved }) => (
  <span style={{
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: 12,
    fontSize: '0.75rem',
    fontWeight: 700,
    background: approved ? '#dcfce7' : '#fef3c7',
    color: approved ? '#166534' : '#92400e',
  }}>
    {approved ? '✔ Approved' : '⏳ Pending'}
  </span>
);

const AdminReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all' | 'pending' | 'approved'
  const [actionLoading, setActionLoading] = useState(null);

  const fetchReviews = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.get('/admin/reviews');
      setReviews(Array.isArray(data) ? data : data.reviews || []);
      setError(null);
    } catch {
      setError('Failed to load reviews.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchReviews(); }, [fetchReviews]);

  const handleApprove = async (id, currentVal) => {
    setActionLoading(id + '-approve');
    try {
      await api.patch(`/admin/reviews/${id}`, { is_approved: currentVal ? 0 : 1 });
      await fetchReviews();
    } catch {
      alert('Failed to update review');
    } finally {
      setActionLoading(null);
    }
  };

  const handleFeature = async (id, currentVal) => {
    setActionLoading(id + '-feature');
    try {
      await api.patch(`/admin/reviews/${id}`, { is_featured: currentVal ? 0 : 1 });
      await fetchReviews();
    } catch {
      alert('Failed to update review');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete review from "${name}"? This cannot be undone.`)) return;
    setActionLoading(id + '-delete');
    try {
      await api.delete(`/admin/reviews/${id}`);
      await fetchReviews();
    } catch {
      alert('Failed to delete review');
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  const displayed = reviews.filter(r => {
    if (filter === 'pending')  return !r.is_approved;
    if (filter === 'approved') return !!r.is_approved;
    return true;
  });

  const pendingCount  = reviews.filter(r => !r.is_approved).length;
  const approvedCount = reviews.filter(r =>  r.is_approved).length;

  return (
    <AdminLayout pageTitle="Reviews">
      <div className="applications-list">
        {/* Header */}
        <div className="list-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.5rem' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.5rem', color: '#0f172a' }}>Reviews</h1>
            <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.875rem' }}>
              {pendingCount} pending approval · {approvedCount} approved
            </p>
          </div>
          <button onClick={fetchReviews} className="btn-view" style={{ fontSize: '0.85rem' }}>↺ Refresh</button>
        </div>

        {/* Filter tabs */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
          {[
            { key: 'all',      label: `All (${reviews.length})` },
            { key: 'pending',  label: `Pending (${pendingCount})` },
            { key: 'approved', label: `Approved (${approvedCount})` },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              style={{
                padding: '0.45rem 1rem',
                borderRadius: 20,
                border: '1.5px solid',
                borderColor: filter === key ? '#0A1F4E' : '#e2e8f0',
                background: filter === key ? '#0A1F4E' : '#fff',
                color: filter === key ? '#fff' : '#64748b',
                fontWeight: 700,
                fontSize: '0.82rem',
                cursor: 'pointer',
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {error && (
          <div className="error-banner" style={{ background: '#fff3f3', border: '1px solid #fca5a5', borderRadius: 8, padding: '12px 16px', color: '#7f1d1d', marginBottom: '1rem' }}>
            {error}
          </div>
        )}

        {loading ? (
          <div className="loading-container">
            <div className="spinner-large" />
            <p>Loading reviews…</p>
          </div>
        ) : displayed.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem 1rem', color: '#94a3b8' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>⭐</div>
            <p style={{ margin: 0, fontWeight: 600 }}>
              {filter === 'pending' ? 'No reviews pending approval' : 'No reviews found'}
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {displayed.map(rev => (
              <div
                key={rev.id}
                style={{
                  background: '#fff',
                  border: rev.is_approved ? '1px solid #e2e8f0' : '2px solid #f59e0b',
                  borderRadius: 12,
                  padding: '1.25rem 1.5rem',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                }}
              >
                {/* Top row */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <strong style={{ fontSize: '1rem', color: '#0f172a' }}>{rev.reviewer_name || 'Anonymous'}</strong>
                      {rev.reviewer_area && <span style={{ fontSize: '0.8rem', color: '#64748b' }}>· {rev.reviewer_area}</span>}
                      {rev.source === 'google' && (
                        <span style={{ fontSize: '0.72rem', background: '#e8f0fe', color: '#1a73e8', padding: '2px 8px', borderRadius: 10, fontWeight: 700 }}>Google</span>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: 4 }}>
                      <div>{STAR(5, rev.rating || 0)}</div>
                      <span style={{ fontSize: '0.78rem', color: '#94a3b8' }}>{formatDate(rev.created_at)}</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <StatusBadge approved={rev.is_approved} />
                    {rev.is_featured ? (
                      <span style={{ fontSize: '0.72rem', background: '#ede9fe', color: '#6d28d9', padding: '3px 10px', borderRadius: 12, fontWeight: 700 }}>⭐ Featured</span>
                    ) : null}
                  </div>
                </div>

                {/* Review content */}
                {rev.title && (
                  <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e293b', marginBottom: '0.35rem' }}>{rev.title}</div>
                )}
                <div style={{ fontSize: '0.9rem', color: '#374151', lineHeight: 1.65, marginBottom: '0.75rem', background: '#f8fafc', borderRadius: 8, padding: '10px 14px' }}>
                  {rev.body}
                </div>

                {rev.instructor_name && (
                  <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '0.75rem' }}>
                    Instructor mentioned: <strong>{rev.instructor_name}</strong>
                  </div>
                )}

                {/* Action buttons */}
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <button
                    className={rev.is_approved ? 'btn-view' : 'btn-approve'}
                    disabled={actionLoading === rev.id + '-approve'}
                    onClick={() => handleApprove(rev.id, rev.is_approved)}
                  >
                    {actionLoading === rev.id + '-approve'
                      ? '⏳'
                      : rev.is_approved ? 'Unapprove' : '✔ Approve'}
                  </button>
                  <button
                    className="btn-view"
                    disabled={actionLoading === rev.id + '-feature'}
                    onClick={() => handleFeature(rev.id, rev.is_featured)}
                    style={{ background: rev.is_featured ? '#ede9fe' : undefined, color: rev.is_featured ? '#6d28d9' : undefined }}
                  >
                    {actionLoading === rev.id + '-feature'
                      ? '⏳'
                      : rev.is_featured ? 'Unfeature' : '⭐ Feature'}
                  </button>
                  <button
                    className="btn-reject"
                    disabled={actionLoading === rev.id + '-delete'}
                    onClick={() => handleDelete(rev.id, rev.reviewer_name)}
                  >
                    {actionLoading === rev.id + '-delete' ? '⏳' : '✖ Delete'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminReviews;
